import {Button, CircularProgress, Grid, Typography} from "@mui/material"
import React, {useContext, useState} from "react"
import {AppContext} from "../../contexts/AppContextProvider"
import Field from "../Field"
import {useFormik} from 'formik';
import * as yup from 'yup';
import useScreenFlow from "../../hooks/useScreenFlow";
import http from "../../http";
import {Customer} from "../../contexts/types";
import charLimit from "../../lib/charLimit"
import {OrgCustomizationContext} from "../../themes/OrgCustomizationProvider";
import makeStyles from "@mui/styles/makeStyles";
import {updateGeneralInfo} from "../../contexts/GeneralInfoReducer";
import CountrySelectInput from "../Inputs/CountrySelectInput";
import {getSearchParamsCountryCode} from "../../lib/locale-helpers";
import {patchGuestInfo} from "../../contexts/GuestInfoReducer";
import {isEmpty} from "lodash";

const useStyles = makeStyles({
    removeNumberIncrementButtons: {
        '& input[type=number]': {
            '-moz-appearance': 'textfield'
        },
        '& input[type=number]::-webkit-outer-spin-button': {
            '-webkit-appearance': 'none',
            margin: 0
        },
        '& input[type=number]::-webkit-inner-spin-button': {
            '-webkit-appearance': 'none',
            margin: 0
        }
    },
});

const GuestForm = () => {
    const {complaint, dispatch, setCustomer} = useContext(AppContext)
    const {t, theme, changeLanguage, supportedLanguages, guestCountryWhiteList, countryLanguageMap} = useContext(OrgCustomizationContext)
    const classes = useStyles()
    const {navigateTo} = useScreenFlow();
    const [customerNumberMatch, setCustomerNumberMatch] = useState<boolean | null>(null)
    const [loadingCustomerSearch, setLoadingCustomerSearch] = useState<boolean>(false)
    const req = t('this_field_is_required')
    const emailFormat = t('gi_invalid_email')
    const maxChar = (value: number): string => t('gi_limit_of', {characters: value.toString()})
  
    const handleCountryChange = (countryCode: string) => {
        let languageCode = countryLanguageMap[countryCode]
    
        // If the language code is not supported, default to English
        if (!languageCode || !supportedLanguages.has(languageCode)) {
          languageCode = 'en'
        }
    
        // Update the application language
        changeLanguage(languageCode)
    
        // Update the URL parameter
        const url = new URL(window.location.toString())
        url.searchParams.set('lang', `${languageCode}-${countryCode}`)
        window.history.pushState(null, '', url.toString())

        // Update Formik with the country name
        formik.setFieldValue('country', countryCode, true)
      }

    const validationSchema = yup.object({
        customerNumber: yup
            .number()
            .required(req)
            .integer(req),
        customerName: yup
            .string()
            .required(req)
            .max(40, maxChar(40)),
        contactName: yup
            .string()
            .required(req),
        email: yup
            .string()
            .email(emailFormat)
            .required(req)
            .max(80, maxChar(80)),
        telephone: yup
            .string()
            .matches(/^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/, t('invalid_phone_number'))
            .required(req),
        country: yup
            .string()
            .required()
    })

    const initialValues = {
        customerNumber: '',
        customerName: '',
        contactName: '',
        email: '',
        telephone: '',
        country: (complaint.guestInfo.country === 'int' ? '' : getSearchParamsCountryCode()) || ''
    }

    const formik = useFormik({

        initialValues: initialValues,
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            setCustomerNumberMatch(null)

            const customerNumber = parseInt(values.customerNumber)

            try {
                setLoadingCustomerSearch(true)

                const {data: {customerExists}} = await http.get<{
                    customerExists: boolean
                }>(`/api/Customer/Search?customerNumber=${customerNumber}&countryCode=${values.country}`)

                setCustomerNumberMatch(customerExists)

                if (customerExists) {
                    dispatch(updateGeneralInfo({
                        contactName: values.contactName,
                        isAcknowledged: complaint.generalInfo.isAcknowledged,
                    }))

                    setCustomer({
                        customer: String(values.customerNumber),
                        firstName: '',
                        lastName: '',
                        emailAddress: values.email,
                        shipToParty: values.customerNumber,
                        billToParty: '',
                        name1: values.customerName,
                        name2: '',
                        country: values.country,
                        salesOrg: '',
                        distributionChannel: '',
                        division: '',
                        shippingMethod: '',
                        paymentTerm: '',
                        language: '',
                        currency: '',
                        policyChkText: '',
                        status: undefined,
                        errorText: undefined,
                    } as Customer)

                    dispatch(patchGuestInfo.telephone(values.telephone.replace(/\D/g, values.telephone)))
                    dispatch(patchGuestInfo.customerNumber(values.customerNumber.toString()))
                    dispatch(patchGuestInfo.email(values.email))
                    dispatch(patchGuestInfo.country(values.country))

                    document.cookie = `X-User-Id=${values.customerNumber}; path=/; SameSite=Strict`;
                    document.cookie = `X-User-Email=${values.email}; path=/; SameSite=Strict`;

                    navigateTo('/guest')
                }
            } catch (e) {
                console.log(e)
            } finally {
                setLoadingCustomerSearch(false)
            }
        },
    })

    return (
        <>
            <Typography
                variant='h5'
                style={{
                    color: theme.palette.primary.main,
                    marginBottom: '1rem'
                }}
            >
                {t('gi_customer_information')}
            </Typography>

            <form onSubmit={formik.handleSubmit}>

                <Grid
                    container
                    xs={12}
                    sm={12}
                    md={9}
                    lg={6}
                    xl={6}
                    sx={{
                        backgroundColor: 'rgba(0,0,0,.05)',
                    }}
                    justifyContent='center'
                    alignSelf='center'
                >

                    <Grid item xs={9} sm={9} md={9} lg={9} sx={{marginRight: '2rem'}}>
                        {
                            getSearchParamsCountryCode() === 'int' &&
                            <CountrySelectInput name='country'
                                                label={t('gi_country')}
                                                value={formik.values.country}
                                                onChange={(e) => {
                                                    formik.handleChange(e)
                                                    handleCountryChange(e.target.value);
                                                  }}
                                                whitelist={guestCountryWhiteList}
                                                error={!!formik.touched.country && !!formik.errors.country}
                                                helperText={formik.touched.country && formik.errors.country}/>
                        }

                        <Field
                            type='number'
                            id="customerNumber"
                            name="customerNumber"
                            label={t('gi_customer_number') + '*'}
                            variant="outlined"
                            className={classes.removeNumberIncrementButtons}
                            value={formik.values.customerNumber}
                            error={
                                formik.touched.customerNumber && !loadingCustomerSearch
                                    ? (customerNumberMatch === false && isEmpty(formik.errors)) || Boolean(formik.errors.customerNumber)
                                    : false
                            }
                            helperText={
                                formik.touched.customerNumber && !loadingCustomerSearch && customerNumberMatch === false
                                    ? isEmpty(formik.errors) ? `${t('gi_invalid_customer')}` : undefined
                                    : undefined
                            }
                            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => formik.handleChange(e)}
                            onInput={(e: any) => {
                                const maxLen: number = 10;
                                const sliceEnd: number = maxLen;
                                const val: string = String(e.target.value)
                                const len: number = val.length

                                e.target.value = len > maxLen ? val.slice(0, sliceEnd) : val;
                            }}
                        />
                        <Field
                            id="customerName"
                            label={t('gi_customer_name') + '*'}
                            name="customerName"
                            variant="outlined"
                            value={formik.values.customerName}
                            error={
                                formik.touched.customerName
                                &&
                                Boolean(formik.errors.customerName)
                            }
                            helperText={
                                formik.touched.customerName
                                &&
                                formik.errors.customerName
                            }
                            onChange={(e) => {

                                let string = e.currentTarget.value

                                const max = 40

                                const result = charLimit({string, max})

                                formik.setFieldValue('customerName', result, true)

                            }}
                        />
                        <Field
                            id="contactName"
                            label={t('gi_contact_name') + '*'}
                            name="contactName"
                            variant="outlined"
                            value={formik.values.contactName}
                            error={
                                formik.touched.contactName
                                &&
                                Boolean(formik.errors.contactName)
                            }
                            helperText={
                                formik.touched.contactName
                                &&
                                formik.errors.contactName
                            }
                            onChange={(e) => {

                                let string = e.currentTarget.value

                                const max = 40

                                const result = charLimit({string, max})

                                formik.setFieldValue('contactName', result, true)

                            }}
                        />
                        <Field
                            id="email"
                            label={t('gi_emailAddress') + '*'}
                            name="email"
                            variant="outlined"
                            value={formik.values.email}
                            error={
                                formik.touched.email
                                &&
                                Boolean(formik.errors.email)
                            }
                            helperText={
                                formik.touched.email
                                &&
                                formik.errors.email
                            }
                            onChange={(e) => {

                                let string = e.currentTarget.value

                                const max = 80

                                const result = charLimit({string, max})

                                formik.setFieldValue('email', result, true)

                            }}
                        />
                        <Field
                            id="telephone"
                            label={t('gi_telephone') + '*'}
                            name="telephone"
                            variant="outlined"
                            value={formik.values.telephone}
                            error={
                                formik.touched.telephone
                                &&
                                Boolean(formik.errors.telephone)
                            }
                            helperText={
                                formik.touched.telephone
                                &&
                                formik.errors.telephone
                            }
                            onChange={formik.handleChange}
                        />

                        <Grid container justifyContent='flex-end'>
                            <Grid item xs={4} sm={4} md={4} lg={3}>
                                <Button
                                    color="primary"
                                    variant="contained"
                                    type="submit"
                                    disabled={loadingCustomerSearch}
                                    fullWidth
                                    sx={{marginBottom: '1rem', marginLeft: '1rem'}}
                                    endIcon={<>{loadingCustomerSearch && <CircularProgress size={15}/>}</>}
                                >
                                    <Typography variant='button'>{t('continue')}</Typography>
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </form>
        </>
    )
}

export default GuestForm
